import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  locationDetailingProductPath,
  locationDetailingProductsPath,
} from "@/routes";
import type { Product } from "./types";

export default function ProductForm({ product }: { product: Product | null }) {
  const { auth, errors } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const title = t(product ? "inventory.edit" : "inventory.add");
  return (
    <AppLayout
      breadcrumbs={[
        {
          title: t("inventory.title"),
          href: locationDetailingProductsPath(locationId),
        },
      ]}
    >
      <Head title={title} />
      <PageCanvas>
        <h1 className="text-page font-display">{title}</h1>
        <Card>
          <CardContent className="pt-6">
            <Form
              key={product?.revision || "new"}
              action={
                product
                  ? locationDetailingProductPath(locationId, product.id)
                  : locationDetailingProductsPath(locationId)
              }
              method={product ? "patch" : "post"}
            >
              {({ processing }) => (
                <div className="space-y-4">
                  {Object.keys(errors || {}).length > 0 && (
                    <div role="alert" className="text-destructive text-sm">
                      {Object.values(errors).map((error, i) => (
                        <p key={i}>{error}</p>
                      ))}
                    </div>
                  )}
                  {product && (
                    <input
                      type="hidden"
                      name="revision"
                      value={product.revision}
                    />
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="product-name">{t("inventory.name")}</Label>
                    <Input
                      autoFocus
                      required
                      id="product-name"
                      name="detailing_product[name]"
                      defaultValue={product?.name || ""}
                      maxLength={200}
                      aria-invalid={!!errors?.name}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="product-unit">{t("inventory.unit")}</Label>
                    {product ? (
                      <Input
                        id="product-unit"
                        readOnly
                        value={t(`inventory.${product.unit}`)}
                        aria-describedby="unit-help"
                      />
                    ) : (
                      <Select
                        name="detailing_product[unit]"
                        defaultValue="litre"
                        items={{
                          litre: t("inventory.litre"),
                          piece: t("inventory.piece"),
                        }}
                      >
                        <SelectTrigger
                          id="product-unit"
                          aria-describedby="unit-help"
                          aria-invalid={!!errors?.unit}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="litre">
                            {t("inventory.litre")}
                          </SelectItem>
                          <SelectItem value="piece">
                            {t("inventory.piece")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <p id="unit-help" className="text-muted-foreground text-sm">
                      {t("inventory.unit_locked")}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {t("inventory.save")}
                    </Button>
                    <Button
                      variant="outline"
                      render={
                        <Link
                          href={
                            product
                              ? locationDetailingProductPath(
                                  locationId,
                                  product.id,
                                )
                              : locationDetailingProductsPath(locationId)
                          }
                        />
                      }
                    >
                      {t("inventory.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
          </CardContent>
        </Card>
      </PageCanvas>
    </AppLayout>
  );
}
